// Ticket attachments: picking, pasting, dropping and
// reordering files before send, and viewing/downloading sent ones.
// A MIXIN merged onto BOTH web-dashboard (the new-ticket dialog, kind 'new')
// and web-ticket-view (the reply compose, kind 'reply'); `this` is whichever
// component instance, and all state (picked lists, drag markers, blob URL
// cache) is instance-local.
export default {

    // Picked-but-unsent files for a compose area: the reply footer ('reply')
    // or the new-ticket dialog ('new').
    _pickedList(kind) {
      return kind === 'new' ? this.newTicketFiles : this.replyFiles;
    },

    // Async on purpose: leanweb runs update() synchronously after any handler
    // that doesn't return a promise, and an update pass while the native file
    // dialog is open risks touching the input mid-choose. The value is also
    // cleared BEFORE opening the picker: if a change event is ever lost (a
    // race, an error mid-handler), a retained value means re-picking the same
    // file never fires change again — the input would stay wedged until a
    // page reload.
    async pickFiles(kind) {
      const scope = kind === 'new' ? '.new-ticket-dialog' : '.ticket-reply';
      const input = this.querySelector(scope + ' input[type=file]');
      if (!input) return;
      input.value = '';
      input.click();
    },

    // Client-side mirror of the server caps (ticket.go: 5 files per message,
    // 10 MB each) so mistakes surface before the upload; the server still
    // enforces both.
    onFilesPicked(kind, input) {
      this.addPickedFiles(kind, input.files);
      // Reset so removing a file and picking it again fires another change.
      input.value = '';
      this.update();
    },

    // Shared by the file picker and clipboard paste: client-side mirror of
    // the server caps (ticket.go: 5 files per message, 10 MB each).
    addPickedFiles(kind, files) {
      const list = this._pickedList(kind);
      for (const f of Array.from(files)) {
        if (list.length >= 5) {
          this.showToast('At most 5 attachments per message', 'danger');
          break;
        }
        if (f.size > 10 * 1024 * 1024) {
          this.showToast(`"${f.name}" is larger than 10 MB`, 'danger');
          continue;
        }
        if (f.size === 0) {
          this.showToast(`"${f.name}" is empty`, 'danger');
          continue;
        }
        list.push(f);
      }
    },

    // Cmd/Ctrl+V in a compose box: clipboard files (screenshots, copied
    // files) become attachments through the same path as the picker; a plain
    // text paste falls through untouched. Every pasted screenshot arrives
    // named "image.png", so those get a distinct timestamped name to keep
    // the chips tellable apart.
    onPaste(kind, e) {
      const files = e.clipboardData && e.clipboardData.files;
      if (!files || files.length === 0) return;
      e.preventDefault();
      const named = Array.from(files).map(f => {
        if (!/^image\.(png|jpe?g|gif|webp)$/i.test(f.name || '')) return f;
        const ext = f.name.split('.').pop().toLowerCase();
        const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        return new File([f], `pasted-${stamp}.${ext}`, { type: f.type });
      });
      this.addPickedFiles(kind, named);
      this.update();
    },

    // Dragging files over a compose area: cancelling dragover is what makes
    // the browser allow a drop at all. Gated on the Files type so dragging
    // selected TEXT over the box keeps its native behavior.
    onDragOver(kind, e) {
      // An in-flight chip reorder: accept the position (without the file
      // drop-zone highlight) so releasing anywhere in the compose area
      // counts as a completed drop — otherwise the browser plays its
      // snap-back animation even though the reorder already happened.
      if (this._chipDrag && this._chipDrag.kind === kind) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        return;
      }
      const types = e.dataTransfer ? Array.from(e.dataTransfer.types) : [];
      if (!types.includes('Files')) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
      if (this.dragTarget !== kind) {
        this.dragTarget = kind;
        this.update();
      }
    },

    onDragLeave(e) {
      // Moving over a child of the drop zone fires dragleave too; only a
      // real exit clears the highlight.
      if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
      if (this.dragTarget) {
        this.dragTarget = '';
        this.update();
      }
    },

    // Dropped files join the message through the same path as the picker
    // and paste (names kept — unlike screenshots, dragged files have real
    // ones).
    onDrop(kind, e) {
      // Completing a chip reorder: the list is already in its final order
      // (live reorder on dragover); accepting the drop just tells the
      // browser the drag succeeded.
      if (this._chipDrag && this._chipDrag.kind === kind) {
        e.preventDefault();
        this._chipDropped = { kind: this._chipDrag.kind, file: this._chipDrag.file };
        this._chipDrag = null;
        // The first real pointer move ends the "just dropped" state and
        // hands highlighting back to normal hover.
        document.addEventListener('mousemove', () => {
          this._chipDropped = null;
          this.update();
        }, { once: true });
        this.update();
        return;
      }
      const files = e.dataTransfer && e.dataTransfer.files;
      this.dragTarget = '';
      if (files && files.length > 0) {
        e.preventDefault();
        this.addPickedFiles(kind, files);
      }
      this.update();
    },

    // Reordering picked chips by drag: the list order is the order the files
    // append to the form, which becomes the server-side seq — so the chips'
    // order before sending is the order the thread will show. Live reorder:
    // dragging chip A over chip B moves A into B's slot immediately. These
    // drags carry no Files type, so the compose drop zones ignore them.
    onChipDragStart(kind, f, e) {
      // The starting order comes along so a cancelled drag can restore it.
      this._chipDrag = { kind, file: f, original: this._pickedList(kind).slice() };
      this._chipDropped = null;
      e.dataTransfer.effectAllowed = 'move';
      // Firefox refuses to start a drag with an empty data store.
      e.dataTransfer.setData('text/plain', f.name);
      // Deferred: touching the DOM synchronously inside dragstart makes
      // Chrome abort the drag. The re-render applies the .dragging chip
      // style and the container's hover suppression.
      setTimeout(() => this.update());
    },

    // The chip carrying the file in flight — the marker follows the FILE
    // through live reorders, unlike :hover, which sticks to whatever node
    // sits under the cursor.
    isChipDragging(f) {
      return !!this._chipDrag && this._chipDrag.file === f;
    },

    // The chip just dropped: it stays the highlighted one until the pointer
    // moves on. Left to :hover alone, the post-reorder layout decides which
    // chip sits under the cursor — often a neighbor, which then lights up.
    isChipDropped(f) {
      return !!this._chipDropped && this._chipDropped.file === f;
    },

    // Hover feedback stays suppressed from grab until the pointer moves
    // after the drop.
    isChipReordering(kind) {
      const active = this._chipDrag || this._chipDropped;
      return !!active && active.kind === kind;
    },

    onChipDragOver(kind, f, e) {
      const drag = this._chipDrag;
      if (!drag || drag.kind !== kind) return;
      // Accept even over the dragged chip itself, so releasing there is a
      // completed drop rather than a snap-back.
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (drag.file === f) return;
      const list = this._pickedList(kind);
      const from = list.indexOf(drag.file);
      const to = list.indexOf(f);
      if (from < 0 || to < 0 || from === to) return;
      list.splice(from, 1);
      list.splice(to, 0, drag.file);
      this.update();
    },

    onChipDragEnd(e) {
      const drag = this._chipDrag;
      this._chipDrag = null;
      if (!drag) return;
      // Released outside every accepting target: the browser plays its
      // fly-back animation, so match it by restoring the starting order —
      // the animation and the outcome then tell the same story.
      if (e.dataTransfer && e.dataTransfer.dropEffect === 'none') {
        const list = this._pickedList(drag.kind);
        list.splice(0, list.length, ...drag.original);
      }
      this.update();
    },

    removePickedFile(kind, file, event) {
      // The chip around this button opens a preview on click; removing a
      // file must not also do that.
      if (event) event.stopPropagation();
      const list = this._pickedList(kind);
      const i = list.indexOf(file);
      if (i >= 0) {
        this._revokePicked([file]);
        list.splice(i, 1);
      }
      this.update();
    },

    // Object URL for a picked file's inline preview (images only), created
    // lazily during render and cached on the File object. Revoked via
    // _revokePicked whenever the file leaves its list.
    pickedPreview(f) {
      if (!f.type || !f.type.startsWith('image/')) return '';
      if (!f._previewUrl) f._previewUrl = URL.createObjectURL(f);
      return f._previewUrl;
    },

    // Types the browser can render in a tab, for pre-send previews of picked
    // files. Broader than the inline-image allowlist: the object URL carries
    // the File's own type, and PDFs and plain text preview fine. Display-only
    // — the server still sniffs and gates what IT serves after upload.
    isViewableFile(f) {
      return /^image\/|^application\/pdf$|^text\/plain$/.test(f.type || '');
    },

    // Open a picked-but-unsent file full size in a new tab (clicking its
    // chip). Reuses the cached object URL (the chip thumbnail's, for images)
    // or creates one; either way _revokePicked retires it when the file
    // leaves the list. Synchronous window.open keeps popup blockers away.
    viewPickedFile(f) {
      if (!this.isViewableFile(f)) return;
      if (!f._previewUrl) f._previewUrl = URL.createObjectURL(f);
      window.open(f._previewUrl, '_blank');
    },

    _revokePicked(files) {
      for (const f of files || []) {
        if (f._previewUrl) {
          URL.revokeObjectURL(f._previewUrl);
          delete f._previewUrl;
        }
      }
    },

    // Mirror of the server's inline allowlist: only these render as <img>
    // previews, everything else is a download chip.
    isImageAttachment(a) {
      return ['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(a.mime);
    },

    attachmentUrl(a) {
      return this._attachmentUrls[a.id] || '';
    },

    revokeAttachmentUrls() {
      for (const id in this._attachmentUrls) URL.revokeObjectURL(this._attachmentUrls[id]);
      this._attachmentUrls = {};
    },

    // Fetch image attachments as blobs and cache object URLs for the inline
    // thumbs — fetch-as-blob because the API needs the Authorization header,
    // which an <img src> can't carry (same pattern as invoiceBlobUrl).
    // Re-runs after every ticket refresh, fetching only what's missing.
    async loadAttachmentPreviews() {
      const ticketID = this.ticketDetail.id;
      const pending = [];
      for (const m of this.ticketMessages) {
        for (const a of m.attachments || []) {
          if (this.isImageAttachment(a) && !this._attachmentUrls[a.id]) pending.push(a);
        }
      }
      if (pending.length === 0) return;
      await Promise.all(pending.map(async a => {
        try {
          const response = await this.apiFetch(this.apiUrl('ticket/attachment?id=' + encodeURIComponent(a.id)));
          if (!response.ok) return;
          const url = URL.createObjectURL(await response.blob());
          // Navigated to another ticket while this fetch was in flight.
          if (this.ticketDetail.id !== ticketID) {
            URL.revokeObjectURL(url);
            return;
          }
          this._attachmentUrls[a.id] = url;
        } catch (e) {}
      }));
      if (this.ticketDetail.id === ticketID) this.update();
    },

    // Open an image preview full size in a new tab. Thumbs only render once
    // their blob URL is cached, so no fetch is needed here — and the
    // synchronous window.open keeps popup blockers out of the way.
    viewAttachment(a) {
      const url = this._attachmentUrls[a.id];
      if (url) window.open(url, '_blank');
    },

    // Sent-attachment mirror of isViewableFile: types safe to open in a tab
    // from a blob URL. SVG and HTML stay download-only — a blob document
    // inherits the app's origin, so markup that can script must never render.
    // Sniffed mimes carry a charset suffix ("text/plain; charset=utf-8"),
    // hence the prefix match.
    isViewableAttachment(a) {
      const mime = a.mime || '';
      return this.isImageAttachment(a) || mime === 'application/pdf' || mime.startsWith('text/plain');
    },

    // Chip click: viewable types open in a tab, the rest download. The chip's
    // own download button (chip-download) forces the download either way.
    openOrDownloadAttachment(a) {
      if (this.isViewableAttachment(a)) this.openAttachment(a);
      else this.downloadAttachment(a);
    },

    downloadAttachmentFromChip(a, event) {
      // The chip around this button opens the file; downloading must not
      // also do that.
      if (event) event.stopPropagation();
      this.downloadAttachment(a);
    },

    // Open a viewable attachment (PDF, text) in a new tab. The blob fetch is
    // async but popup blockers only allow window.open inside the click
    // gesture, so the tab opens first and is pointed at the blob once it
    // arrives. The URL joins the ticket's preview cache, so reopening is
    // instant and revocation rides the existing ticket-switch cleanup.
    async openAttachment(a) {
      const w = window.open('', '_blank');
      try {
        let url = this._attachmentUrls[a.id];
        if (!url) {
          const response = await this.apiFetch(this.apiUrl('ticket/attachment?id=' + encodeURIComponent(a.id)));
          if (!response.ok) {
            const msg = await response.json();
            if (w) w.close();
            this.showToast(msg, 'danger');
            return;
          }
          url = URL.createObjectURL(await response.blob());
          this._attachmentUrls[a.id] = url;
        }
        if (w) w.location = url;
        else window.open(url, '_blank');
      } catch (e) {
        if (w) w.close();
        this.showToast(e.message, 'danger');
      }
    },

    // Fetch an attachment and trigger a browser download (mirrors
    // downloadInvoice; cached preview URLs are reused and kept alive).
    async downloadAttachment(a) {
      try {
        let url = this._attachmentUrls[a.id];
        const cached = !!url;
        if (!cached) {
          const response = await this.apiFetch(this.apiUrl('ticket/attachment?id=' + encodeURIComponent(a.id)));
          if (!response.ok) {
            const msg = await response.json();
            this.showToast(msg, 'danger');
            return;
          }
          url = URL.createObjectURL(await response.blob());
        }
        const el = document.createElement('a');
        el.href = url;
        el.download = a.filename || 'file';
        el.click();
        // Defer the revoke: revoking synchronously after click() can race the
        // download navigation in Safari (see downloadInvoice).
        if (!cached) setTimeout(() => URL.revokeObjectURL(url), 10000);
      } catch (e) {
        this.showToast(e.message, 'danger');
      }
    },
};
